import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Group,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconServer,
  IconShieldCheck,
  IconBolt,
  IconTerminal2,
} from "@tabler/icons-react";
import { useAuth } from "../../auth/AuthContext.js";
import { friendlyError } from "../../api/client.js";
import { Logo } from "../../components/Logo.js";
import { ThemeToggle } from "../../components/ThemeToggle.js";

export function CustomerLogin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const role = await auth.login(username.trim(), password);
      notifications.show({ color: "green", message: "Signed in" });
      if (role === "admin") {
        // admin creds on the customer page — bounce to admin console
        window.location.href = "/admin";
        return;
      }
      navigate("/dashboard", { replace: true });
      return;
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Sign-in failed",
        message: friendlyError(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Already-authenticated customer arriving at /login (e.g. back button)
  // goes straight to the dashboard instead of looping here.
  if (auth.status === "authenticated" && auth.session?.role === "customer") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box style={{ display: "flex", minHeight: "100vh" }}>
      {/* Brand hero — hidden on narrow viewports */}
      <BrandPanel />

      {/* Form side */}
      <Box
        component="section"
        style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}
      >
        {/* Theme toggle in the corner */}
        <Box style={{ position: "absolute", top: 16, right: 16 }}>
          <ThemeToggle />
        </Box>

        <Center style={{ flex: 1 }} p="md">
          <Stack w={{ base: 320, sm: 380, md: 420 }} gap="md">
            {/* Mobile-only brand lockup */}
            <Box hiddenFrom="md" mb="xs">
              <Logo size={48} />
            </Box>

            <Stack gap={4}>
              <Title order={2}>Sign in</Title>
              <Text size="sm" c="dimmed">
                Welcome back — manage your virtual machines.
              </Text>
            </Stack>

            <Box component="form" onSubmit={onSubmit}>
              <Stack gap="sm">
                <TextInput
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.currentTarget.value)}
                  required
                  autoComplete="username"
                  placeholder="your.username"
                  autoFocus
                />
                <PasswordInput
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                  autoComplete="current-password"
                />
                <Button type="submit" loading={submitting} fullWidth size="md" mt="xs">
                  Sign in
                </Button>
              </Stack>
            </Box>

            <Text size="xs" c="dimmed" ta="center" mt="sm">
              Need access? Contact your account manager.
            </Text>
          </Stack>
        </Center>
      </Box>
    </Box>
  );
}

/**
 * Brand panel — left-hand hero for the customer login. Visible only on
 * medium-and-up viewports where there's room for it. Uses CSS variables
 * that resolve per color scheme so the gradient looks intentional in
 * both light and dark mode.
 */
function BrandPanel() {
  return (
    <Box
      component="aside"
      visibleFrom="md"
      style={{
        flex: "0 0 460px",
        maxWidth: 460,
        position: "relative",
        background:
          "linear-gradient(155deg, var(--mantine-color-brand-7), var(--mantine-color-brand-9) 60%, var(--mantine-color-dark-9))",
        color: "white",
        overflow: "hidden",
      }}
    >
      {/* Decorative grid pattern overlay */}
      <Box
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      <Box
        p="xl"
        style={{ position: "relative", display: "flex", flexDirection: "column", minHeight: "100%" }}
      >
        {/* Logo at top — the official lockup (transparent), straight on
            the gradient. No chip: the PNG's transparency is the design. */}
        <Logo size={72} />

        {/* Hero copy in the middle */}
        <Center style={{ flex: 1 }}>
          <Stack gap="lg" maw={360}>
            <Stack gap={0}>
              <Title order={1} c="white" style={{ fontSize: "2.25rem", lineHeight: 1.15 }}>
                Cloud-grade compute, on your terms.
              </Title>
              <Text size="md" c="white" opacity={0.85}>
                Provision, scale and recover KVM virtual machines in seconds — full console access, snapshot-aware backups, and bandwidth you control.
              </Text>
            </Stack>

            <SimpleGrid cols={2} spacing="md" mt="sm">
              <FeatureBullet
                icon={<IconBolt size={18} stroke={1.75} />}
                title="Fast deploy"
                desc="VMs in seconds"
              />
              <FeatureBullet
                icon={<IconTerminal2 size={18} stroke={1.75} />}
                title="Full console"
                desc="Native noVNC access"
              />
              <FeatureBullet
                icon={<IconShieldCheck size={18} stroke={1.75} />}
                title="Secure by default"
                desc="Isolated tenancy"
              />
              <FeatureBullet
                icon={<IconServer size={18} stroke={1.75} />}
                title="Real hardware"
                desc="Dedicated Proxmox nodes"
              />
            </SimpleGrid>
          </Stack>
        </Center>

        {/* Footer fine-print */}
        <Text size="xs" c="white" opacity={0.6}>
          &copy; {new Date().getFullYear()} ProxVPS
        </Text>
      </Box>
    </Box>
  );
}

function FeatureBullet({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Box
        style={{
          flex: "0 0 auto",
          width: 32,
          height: 32,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.12)",
        }}
      >
        {icon}
      </Box>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} c="white">
          {title}
        </Text>
        <Text size="xs" c="white" opacity={0.7}>
          {desc}
        </Text>
      </Stack>
    </Group>
  );
}
